import { useEffect, useRef, useState } from 'react'

// UX43: pull-to-refresh for the feed.
//
// Window-level touch gesture. Arms only when scrolled all the way to
// the top (so a normal upward scroll on the middle of the page never
// steals the drag). Shows a progress chevron that flips into a
// spinner the moment the pull passes the trigger threshold, haptic +
// onRefresh fire on release past the threshold.
//
// Pure read model: returns { pullY, refreshing, triggered }. The
// caller draws whatever indicator makes sense. We don't translate the
// page — modern iOS already handles the "rubber-band" so we only
// render a thin indicator strip at the top.

const TRIGGER = 70     // px of pull before release = refresh
const CAP     = 110    // px cap on reported pullY (visual limit)
const DAMPEN  = 0.4    // resistance after passing TRIGGER
const MIN_VIS = 650    // ms minimum spinner visibility

export function usePullToRefresh({ onRefresh, enabled = true }) {
  const [pullY, setPullY] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  // Mirrors the state values so touchend sees current readings without
  // forcing a re-attach of listeners on every pixel.
  const stateRef = useRef({
    startY: 0,
    armed: false,       // touchstart hit while at top
    dragging: false,    // user has moved down from the start
    pullY: 0,
    refreshing: false,
    onRefresh,
  })
  stateRef.current.onRefresh = onRefresh
  stateRef.current.refreshing = refreshing
  stateRef.current.pullY = pullY

  useEffect(() => {
    if (!enabled) return
    // Touch-only — mouse / trackpad pull is noisy and most desktop
    // users have other ways to refresh.
    if (typeof window === 'undefined' || !('ontouchstart' in window)) return

    const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0

    const onStart = (e) => {
      if (stateRef.current.refreshing) return
      if (!atTop()) {
        stateRef.current.armed = false
        return
      }
      stateRef.current.startY = e.touches[0].clientY
      stateRef.current.armed = true
      stateRef.current.dragging = false
    }

    const onMove = (e) => {
      if (!stateRef.current.armed || stateRef.current.refreshing) return
      const dy = e.touches[0].clientY - stateRef.current.startY
      // Negative dy = finger moving up = normal scroll, disarm.
      if (dy <= 0) {
        if (stateRef.current.dragging) {
          stateRef.current.dragging = false
          setPullY(0)
        }
        stateRef.current.armed = atTop()
        return
      }
      if (!atTop()) {
        // User scrolled while pulling — bail.
        stateRef.current.armed = false
        stateRef.current.dragging = false
        setPullY(0)
        return
      }
      stateRef.current.dragging = true
      const damped = dy < TRIGGER ? dy : TRIGGER + (dy - TRIGGER) * DAMPEN
      const next = Math.min(damped, CAP)
      setPullY(next)
      // Suppress native overscroll once we've committed to a pull.
      if (dy > 8 && e.cancelable) e.preventDefault()
    }

    const finish = async () => {
      const wasDragging = stateRef.current.dragging
      const finalPull = stateRef.current.pullY
      stateRef.current.armed = false
      stateRef.current.dragging = false
      if (!wasDragging) {
        setPullY(0)
        return
      }
      if (finalPull >= TRIGGER) {
        try { navigator?.vibrate?.(20) } catch { /* noop */ }
        setRefreshing(true)
        setPullY(TRIGGER * 0.7)
        const started = Date.now()
        try {
          await Promise.resolve(stateRef.current.onRefresh?.())
        } catch { /* noop */ }
        const elapsed = Date.now() - started
        const wait = Math.max(0, MIN_VIS - elapsed)
        setTimeout(() => {
          setRefreshing(false)
          setPullY(0)
        }, wait)
      } else {
        setPullY(0)
      }
    }

    window.addEventListener('touchstart', onStart, { passive: true })
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', finish, { passive: true })
    window.addEventListener('touchcancel', finish, { passive: true })
    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', finish)
      window.removeEventListener('touchcancel', finish)
    }
  }, [enabled])

  return {
    pullY,
    refreshing,
    triggered: pullY >= TRIGGER,
    threshold: TRIGGER,
  }
}
